import { fieldLabel, enumLabel, entityLabel } from '@/lib/labels.js'

/**
 * Fields a condition may name for an entity — the keys of the rule record
 * (CrmRecord::toRuleRecord): plain fields, `<ref>_uuid`, `owner_uuid`, `cf:<key>`, and one
 * level into related records (`rel:account.type`, `rel:stage.kind`).
 */
const RELATIONS = {
  contact: ['account'],
  lead: ['account', 'contact', 'stage'],
  opportunity: ['account', 'contact', 'stage'],
  activity: [],
  offer: ['account', 'opportunity'],
}

function fromField(entity, f, meta, prefix = '', labelPrefix = '') {
  if (f.type === 'morph' || f.type === 'json' || f.type === 'richtext' || f.type === 'address') return null
  const base = { label: labelPrefix + fieldLabel(entity, f.name) }
  if (f.type === 'ref') return { ...base, value: `${prefix}${f.input}`, type: 'ref', ref: f.ref, pipeline: f.ref === 'pipeline' || f.ref === 'pipeline_stage' ? f.ref : null }
  if (f.type === 'enum') return { ...base, value: `${prefix}${f.name}`, type: 'enum', options: (f.options || []).map((o) => ({ value: o, label: enumLabel(entity, f.name, o) })) }
  if (f.type === 'dictionary') return { ...base, value: `${prefix}${f.name}`, type: 'enum', options: meta.dictionary(f.dictionary).entries.map((e) => ({ value: e.key, label: meta.localized(e.label) })) }
  const type = { integer: 'number', decimal: 'number', money: 'number', percent: 'number', date: 'date', datetime: 'date', bool: 'bool' }[f.type] || 'text'
  return { ...base, value: `${prefix}${f.name}`, type }
}

function fromCustom(entity, cf, meta, prefix = '', labelPrefix = '') {
  const base = { value: `${prefix}cf:${cf.key}`, label: labelPrefix + meta.localized(cf.label) }
  if (cf.type === 'select' || cf.type === 'multiselect') {
    const options = cf.options?.length ? cf.options.map((o) => ({ value: o.key, label: meta.localized(o.label) })) : meta.dictionary(cf.dictionary).entries.map((e) => ({ value: e.key, label: meta.localized(e.label) }))
    return { ...base, type: 'enum', options }
  }
  if (cf.type === 'lookup') return { ...base, type: 'ref', ref: cf.target_entity }
  return { ...base, type: { number: 'number', money: 'number', percent: 'number', computed: 'number', date: 'date', datetime: 'date', bool: 'bool' }[cf.type] || 'text' }
}

export function conditionFields(entity, meta, t) {
  const out = []
  for (const f of meta.fields(entity)) {
    const item = fromField(entity, f, meta)
    if (item) out.push(item)
  }
  out.push({ value: 'owner_uuid', label: fieldLabel(entity, 'owner'), type: 'owner' })
  for (const cf of meta.customFields(entity)) out.push(fromCustom(entity, cf, meta))
  for (const relation of RELATIONS[entity] || []) {
    if (relation === 'stage') {
      out.push({ value: 'rel:stage.kind', label: `${fieldLabel(entity, 'stage')} → ${t('settings.kind')}`, type: 'enum', options: ['open', 'won', 'lost', 'qualified', 'disqualified'].map((k) => ({ value: k, label: t(`enums.stageKind.${k}`) })) })
      out.push({ value: 'rel:stage.key', label: `${fieldLabel(entity, 'stage')} → ${t('common.key')}`, type: 'text' })
      continue
    }
    const prefix = `rel:${relation}.`
    const labelPrefix = `${entityLabel(relation)} → `
    for (const f of meta.fields(relation)) {
      if (f.type === 'ref' || f.readonly) continue
      const item = fromField(relation, f, meta, prefix, labelPrefix)
      if (item) out.push(item)
    }
    for (const cf of meta.customFields(relation)) out.push(fromCustom(relation, cf, meta, prefix, labelPrefix))
  }
  return out
}

export const OPERATORS_BY_TYPE = {
  text: ['eq', 'neq', 'contains', 'not_contains', 'starts_with', 'empty', 'not_empty', 'changed'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'empty', 'not_empty', 'changed'],
  date: ['eq', 'gt', 'gte', 'lt', 'lte', 'between', 'within_last_days', 'within_next_days', 'older_than_days', 'empty', 'not_empty', 'changed'],
  enum: ['eq', 'neq', 'in', 'nin', 'empty', 'not_empty', 'changed', 'changed_to'],
  bool: ['eq', 'changed'],
  ref: ['eq', 'neq', 'in', 'empty', 'not_empty', 'changed'],
  owner: ['eq', 'neq', 'is_me', 'in_my_groups', 'empty', 'not_empty', 'changed'],
}

export const NO_VALUE = ['empty', 'not_empty', 'changed', 'is_me', 'in_my_groups']
